import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { getFutureHazardLevelIndex } from "helpers/future-hazard-legend";
import { useTranslation } from "react-i18next";
import TryAgain from "components/tryAgain/tryAgain";

interface Props {
  tableData: any[];
  graunch?: boolean;
  handleRetry: (evt: any) => void;
  type?: "climate" | "futureHazard" | "CRSComponent";
}

interface RangeColorRule {
  upper: number;
  color: string;
  backgroundColor: string;
}

const CLIMATE_COLOR_RULES: RangeColorRule[] = [
  { upper: 2, color: "rgb(39, 83, 23)", backgroundColor: "rgb(181, 230, 162)" },
  {
    upper: 4,
    color: "rgb(78, 167, 46)",
    backgroundColor: "rgb(218, 242, 208)"
  },
  { upper: 6, color: "rgb(156, 0, 6)", backgroundColor: "rgb(255, 255, 204)" },
  { upper: 8, color: "rgb(156, 0, 6)", backgroundColor: "rgb(255, 199, 206)" },
  {
    upper: Number.POSITIVE_INFINITY,
    color: "rgb(255, 255, 255)",
    backgroundColor: "rgb(204, 0, 0)"
  }
];

const FUTURE_HAZARD_TEXT_COLORS = [
  "#000000",
  "#275317",
  "#4ea72e",
  "#9c0006",
  "#9c0006",
  "#9c0006",
  "#ffffff"
];
const FUTURE_HAZARD_BG_COLORS = [
  "#F2F2F2",
  "#b5e6a2",
  "#daf2d0",
  "#ffffcc",
  "#F2AA84",
  "#ffc7ce",
  "#cc0000"
];

const getColorByRules = (num: number, rules: RangeColorRule[]) => {
  return rules.find((rule) => num < rule.upper) || rules[rules.length - 1];
};

const BasicTable = (props: Props) => {
  return (
    <Paper
      sx={{
        width: 780,
        overflow: "auto",
        boxShadow: "none",
        paddingBottom: "12px"
      }}
    >
      <Table
        sx={{ border: "1px solid #d3d3d3" }}
        size="small"
        aria-label="simple table"
      >
        <TableHead sx={{ backgroundColor: "#F2F2F2" }}>
          <TableRow>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
            ></TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2030
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2035
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2040
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2045
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2050
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2055
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2060
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2070
            </TableCell>
            <TableCell
              sx={{ border: "1px solid #d3d3d3", fontSize: "14px" }}
              align="right"
            >
              2085
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <BasicTableBody {...props} />
        </TableBody>
      </Table>
    </Paper>
  );
};

const BasicTableBody = (props: Props) => {
  const [t] = useTranslation();
  const isCrsComponentType = props.type === "CRSComponent";
  const isFutureHazardType = props.type === "futureHazard";
  const isEmptyOrNaN = (value: any) => value === "" || isNaN(value);
  const baseMetricCellSx = {
    border: "1px solid #d3d3d3",
    fontSize: "14px"
  };
  const getLayerLabel = (layerId: string) => {
    const prefix =
      props.type === "futureHazard"
        ? "layerId.futureHazard"
        : props.type === "CRSComponent"
          ? "layerId.CRSComponent"
          : "layerId";
    return t(`${prefix}.${layerId}`);
  };
  const getMetricCellSx = (value: any) => {
    const invalidValue = isEmptyOrNaN(value);
    const numericValue = value * 1;
    if (invalidValue) {
      return {
        ...baseMetricCellSx,
        backgroundColor: "#BFBFBF",
        color: ""
      };
    }

    if (isCrsComponentType) {
      return baseMetricCellSx;
    }

    if (isFutureHazardType) {
      const levelIndex = getFutureHazardLevelIndex(numericValue);
      if (levelIndex) {
        return {
          ...baseMetricCellSx,
          backgroundColor: FUTURE_HAZARD_BG_COLORS[levelIndex],
          color: FUTURE_HAZARD_TEXT_COLORS[levelIndex]
        };
      }

      return baseMetricCellSx;
    }

    const climateRule = getColorByRules(numericValue, CLIMATE_COLOR_RULES);
    return {
      ...baseMetricCellSx,
      backgroundColor: climateRule.backgroundColor,
      color: climateRule.color
    };
  };
  const getMetricCellDisplayValue = (value: any) => {
    return isEmptyOrNaN(value) ? "" : value;
  };
  if (props.graunch) {
    return (
      <TableRow>
        <TableCell colSpan={10}>
          <TryAgain handleRetry={props.handleRetry} />
        </TableCell>
      </TableRow>
    );
  } else if (props.tableData.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={10}>
          <div className="text-center">
            <div className="loading loading-spinner loading-lg"></div>
            <div className="text-[14px]">{t("single.climateTableLoading")}</div>
          </div>
        </TableCell>
      </TableRow>
    );
  } else {
    return (
      <>
        {props.tableData.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell: any, index: number) =>
              index === 0 ? (
                <TableCell
                  key={index}
                  sx={{
                    border: "1px solid #d3d3d3",
                    backgroundColor: "#F2F2F2",
                    fontSize: "14px",
                    whiteSpace: "nowrap"
                  }}
                >
                  {getLayerLabel(cell)}
                </TableCell>
              ) : (
                <TableCell key={index} sx={getMetricCellSx(cell)} align="right">
                  {getMetricCellDisplayValue(cell)}
                </TableCell>
              )
            )}
          </TableRow>
        ))}
      </>
    );
  }
};

export default BasicTable;
